// src/pages/ChangeParticipantPasswordPage.tsx
import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner
} from 'reactstrap';
import { useChangeParticipantPassword } from '../../../../hooks/useChangeParticipantPassword';
import { ArrowLeft, Lock, Key } from 'react-feather';
import { toast } from 'react-toastify';
import { Image } from '../../../../AbstractElements';
import { dynamicImage } from '../../../../Utils';

export default function ChangeParticipantPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // read from location.state (expected shape { userId: string, groupId?: string })
  const locState = (location.state || {}) as any;
  const userId = locState?.userId ?? '';
  const groupId = locState?.groupId ?? '';

  const {
    newPassword,
    confirmPassword,
    loading,
    errors,
    serverError,
    successMsg,
    setNewPassword,
    setConfirmPassword,
    submit,
    reset
  } = useChangeParticipantPassword({ userId, groupId, onSuccess: () => { } });

  useEffect(() => {
    if (!userId) {
      toast.warn('Missing userId in navigation state. Open the link from the invitation flow.');
    }
  }, [userId]);

  const isDark = typeof document !== 'undefined' && (document.body.classList.contains('dark') || document.documentElement.getAttribute('data-theme') === 'dark');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submit();
  };

  return (
    <Container className="p-0" fluid style={{ backgroundColor: 'black' }}>
      <Row className="m-0">
        <Col xs={12} className="p-0">
          <div className="login-card login-dark">
            <div>

              <div>
                <Link className="logo" to={`${process.env.PUBLIC_URL}/dashboard/default`}>
                  <Image
                    height={50}
                    className="for-light"
                    src={dynamicImage(`logo/divine_comedy.webp`)}
                    alt="looginpage"
                  />
                  <Image
                    height={50}
                    className="for-dark"
                    src={dynamicImage(`logo/divine_comedy.webp`)}
                    alt="looginpage"
                  />
                </Link>
              </div>


            <div className="login-main">
              <Form className="theme-form" onSubmit={onSubmit}>

                <div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>Set your password</div>
                  <div style={{ fontSize: 13, opacity: 0.95, marginTop: 4 }}>
                    Secure your account — set a password to sign in later.
                  </div>
                </div>

                <FormGroup>
                  <Label for="newPassword" style={{ fontWeight: 600, color: isDark ? '#e6eef8' : '#111827' }}>
                    New password
                  </Label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginTop: 8,
                    background: isDark ? '#041126' : '#f7fafc',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${errors?.newPassword ? '#f44336' : (isDark ? 'rgba(255,255,255,0.04)' : '#e6eef7')}`
                  }}>
                    <Lock size={18} style={{ opacity: 0.7 }} />
                    <Input
                      id="newPassword"
                      type="password"
                      bsSize="lg"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      aria-label="New password"
                      style={{ border: 'none', outline: 'none', background: 'transparent', color: isDark ? '#e6eef8' : '#111827' }}
                    />
                  </div>
                  {errors?.newPassword && <div style={{ color: '#f44336', marginTop: 8, fontSize: 13 }}>{errors.newPassword}</div>}
                </FormGroup>

                <FormGroup style={{ marginTop: 12 }}>
                  <Label for="confirmPassword" style={{ fontWeight: 600, color: isDark ? '#e6eef8' : '#111827' }}>
                    Confirm password
                  </Label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginTop: 8,
                    background: isDark ? '#041126' : '#f7fafc',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${errors?.confirmPassword ? '#f44336' : (isDark ? 'rgba(255,255,255,0.04)' : '#e6eef7')}`
                  }}>
                    <Key size={18} style={{ opacity: 0.7 }} />
                    <Input
                      id="confirmPassword"
                      type="password"
                      bsSize="lg"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat the password"
                      aria-label="Confirm password"
                      style={{ border: 'none', outline: 'none', background: 'transparent', color: isDark ? '#e6eef8' : '#111827' }}
                    />
                  </div>
                  {errors?.confirmPassword && <div style={{ color: '#f44336', marginTop: 8, fontSize: 13 }}>{errors.confirmPassword}</div>}
                </FormGroup>

                {serverError && <div style={{ color: '#f44336', marginTop: 12 }}>{serverError}</div>}
                {successMsg && <div style={{ color: '#16a34a', marginTop: 12 }}>{successMsg}</div>}

                <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                  <Button color="secondary" outline onClick={() => { reset(); }} disabled={loading}>
                    Clear
                  </Button>

                  <div style={{ marginLeft: 'auto' }}>
                    <Button color="primary" onClick={() => submit()} disabled={loading || !userId}>
                      {loading ? (<><Spinner size="sm" />&nbsp;Updating…</>) : 'Set password'}
                    </Button>
                  </div>
                </div>
              </Form>

              <div style={{ marginTop: 16, color: isDark ? 'rgba(255,255,255,0.6)' : '#6b7280', fontSize: 13 }}>
                Note: Password change is allowed only for inactive participants. If you see an error, contact your admin.
              </div>
            </div>
                        </div>

          </div>
        </Col>
      </Row>
    </Container>
  );
}
